/**
 * useAggregatedToolState Hook
 *
 * Provides aggregated tool state from message parts.
 * Groups all stream chunks by toolCallId into AggregatedToolCall objects.
 *
 * Source: PRD-TOOL-COMPONENTS-REFACTOR.md Section 6.3
 */

import { useMemo } from 'react';
import {
  aggregateToolCallsToArray,
  getToolCallById,
  hasToolParts,
} from '../aggregators/tool-call-aggregator';
import { deriveToolState } from '../types/tools';
import type { AggregatedToolCall, ToolState } from '../types/tools';
import type { SolaMessage, SolaMessagePart } from '../types';

/**
 * Get all aggregated tool calls from a message
 *
 * @param message - The message to extract tool calls from
 * @returns Array of aggregated tool calls
 */
export function useAggregatedToolState(message: SolaMessage): AggregatedToolCall[] {
  return useMemo(() => {
    if (!message?.parts) return [];
    return aggregateToolCallsToArray(message.parts as SolaMessagePart[]);
  }, [message?.parts]);
}

/**
 * Get a specific tool call by ID from a message
 *
 * @param message - The message to search in
 * @param toolCallId - The ID of the tool call to find
 * @returns The aggregated tool call or undefined
 */
export function useToolCallById(
  message: SolaMessage,
  toolCallId: string
): AggregatedToolCall | undefined {
  return useMemo(() => {
    if (!message?.parts || !toolCallId) return undefined;
    return getToolCallById(message.parts as SolaMessagePart[], toolCallId);
  }, [message?.parts, toolCallId]);
}

/**
 * Get derived tool state for UI rendering
 *
 * @param toolCall - The aggregated tool call
 * @returns Derived tool state with convenience properties
 */
export function useToolState<TOutput = unknown>(
  toolCall: AggregatedToolCall<unknown, TOutput> | undefined
): ToolState<TOutput> | null {
  return useMemo(() => {
    if (!toolCall) return null;
    return deriveToolState(toolCall);
  }, [toolCall]);
}

/**
 * Check if a message has any tool invocations
 *
 * @param message - The message to check
 * @returns True if the message contains tool parts
 */
export function useHasTools(message: SolaMessage): boolean {
  return useMemo(() => {
    if (!message?.parts) return false;
    return hasToolParts(message.parts as SolaMessagePart[]);
  }, [message?.parts]);
}

/**
 * Get tool calls with their derived states
 *
 * @param message - The message to extract tool calls from
 * @returns Array of tuples [AggregatedToolCall, ToolState]
 */
export function useToolCallsWithState(
  message: SolaMessage
): Array<[AggregatedToolCall, ToolState]> {
  const toolCalls = useAggregatedToolState(message);

  return useMemo(() => {
    return toolCalls.map(toolCall => [
      toolCall,
      deriveToolState(toolCall),
    ] as [AggregatedToolCall, ToolState]);
  }, [toolCalls]);
}

/**
 * Get only loading tool calls (input-streaming or input-available)
 *
 * @param message - The message to extract tool calls from
 * @returns Array of loading tool calls
 */
export function useLoadingToolCalls(message: SolaMessage): AggregatedToolCall[] {
  const toolCalls = useAggregatedToolState(message);

  return useMemo(() => {
    return toolCalls.filter(
      tc => tc.state === 'input-streaming' || tc.state === 'input-available'
    );
  }, [toolCalls]);
}

/**
 * Get only completed tool calls (output-available with final output)
 *
 * @param message - The message to extract tool calls from
 * @returns Array of completed tool calls
 */
export function useCompletedToolCalls(message: SolaMessage): AggregatedToolCall[] {
  const toolCalls = useAggregatedToolState(message);

  return useMemo(() => {
    return toolCalls.filter(
      tc => tc.state === 'output-available' && tc.finalOutput !== undefined
    );
  }, [toolCalls]);
}

/**
 * Get only error tool calls
 *
 * @param message - The message to extract tool calls from
 * @returns Array of error tool calls
 */
export function useErrorToolCalls(message: SolaMessage): AggregatedToolCall[] {
  const toolCalls = useAggregatedToolState(message);

  return useMemo(() => {
    return toolCalls.filter(
      tc => tc.state === 'output-error' || tc.state === 'output-denied'
    );
  }, [toolCalls]);
}
